import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CalendarIcon, BookOpenIcon, CheckCircleIcon, LockIcon } from 'lucide-react';
import { Badge } from '../hooks/useProgress';
interface BadgeDetailModalProps {
  badge: Badge | null;
  isOpen: boolean;
  onClose: () => void;
}
export function BadgeDetailModal({
  badge,
  isOpen,
  onClose
}: BadgeDetailModalProps) {
  if (!badge) return null;
  const Icon = badge.icon;
  return <AnimatePresence data-id="element-237">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-238">
            {/* Modal Content */}
            <motion.div initial={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative" data-id="element-239">
              {/* Close Button */}
              <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-black/5 hover:bg-black/10 transition-colors z-10" data-id="element-240">
                <XIcon className="w-5 h-5 text-gray-600" data-id="element-241" />
              </button>

              {/* Header / Icon Area */}
              <div className={`
                h-40 flex items-center justify-center relative
                ${badge.isEarned ? `bg-gradient-to-br ${badge.gradient}` : 'bg-gray-100'}
              `} data-id="element-242">
                <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-243"></div>

                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              delay: 0.2
            }} className={`
                    w-24 h-24 rounded-full flex items-center justify-center shadow-xl
                    ${badge.isEarned ? 'bg-white/20 backdrop-blur-md' : 'bg-gray-200'}
                  `} data-id="element-244">
                  <Icon className={`w-12 h-12 ${badge.isEarned ? 'text-white' : 'text-gray-400'}`} data-id="element-245" />
                </motion.div>
              </div>

              {/* Body */}
              <div className="p-8 text-center" data-id="element-246">
                <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-247">
                  {badge.moduleName}
                </h2>
                <p className="text-gray-500 font-medium mb-6" data-id="element-248">
                  {badge.courseName}
                </p>

                <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left" data-id="element-249">
                  <p className="text-gray-700 text-sm leading-relaxed" data-id="element-250">
                    {badge.description}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-251">
                  <div className="flex flex-col items-center p-3 rounded-xl bg-blue-50" data-id="element-252">
                    <span className="text-xs text-blue-600 font-bold uppercase mb-1" data-id="element-253">
                      Status
                    </span>
                    <div className="flex items-center space-x-1" data-id="element-254">
                      {badge.isEarned ? <>
                          <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-255" />
                          <span className="text-sm font-bold text-gray-900" data-id="element-256">
                            Obținut
                          </span>
                        </> : <>
                          <LockIcon className="w-4 h-4 text-gray-400" data-id="element-257" />
                          <span className="text-sm font-bold text-gray-500" data-id="element-258">
                            Blocat
                          </span>
                        </>}
                    </div>
                  </div>

                  <div className="flex flex-col items-center p-3 rounded-xl bg-purple-50" data-id="element-259">
                    <span className="text-xs text-purple-600 font-bold uppercase mb-1" data-id="element-260">
                      Dată
                    </span>
                    <div className="flex items-center space-x-1" data-id="element-261">
                      <CalendarIcon className="w-4 h-4 text-purple-500" data-id="element-262" />
                      <span className="text-sm font-bold text-gray-900" data-id="element-263">
                        {badge.isEarned && badge.earnedAt ? new Date(badge.earnedAt).toLocaleDateString('ro-RO') : '-'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>}
    </AnimatePresence>;
}